import { Gauge, Building2, Receipt } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { Card, StatCard, EmptyState } from '../components/ui';

interface UsageRollup {
  period: string;
  total_units: number;
  by_org: { org_id: string; units: number }[];
  by_ip_pool: { ip_pool_id: string; units: number }[];
  by_period?: { period: string; units: number }[];
  estimated_cost?: {
    currency: string;
    units: number;
    unit_price_cents: number;
    units_cost_cents: number;
    platform_fee_cents: number;
    total_cents: number;
  };
}

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

// Usage — rendered as a tab under Billing (no own PageHeader); scoped to the active Org.
export function UsageSection() {
  const { withOrg, selectedOrgId } = useOrg();
  const { data, error } = useLoad(
    () => api.get<UsageRollup>(withOrg('/v1/usage')),
    [selectedOrgId],
  );

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-lg border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={`Simulation Units · ${data?.period ?? 'all'}`}
          value={String(data?.total_units ?? 0)}
          icon={Gauge}
          hint="one unit per send"
        />
        <StatCard
          label="Estimated cost"
          value={usd(data?.estimated_cost?.total_cents ?? 0)}
          icon={Receipt}
          hint={
            data?.estimated_cost
              ? `${usd(data.estimated_cost.units_cost_cents)} usage + ${usd(data.estimated_cost.platform_fee_cents)} platform fee`
              : 'units × rate + platform fee'
          }
        />
      </div>

      <Card className="mt-4">
        <div className="eyebrow flex items-center gap-2 border-b border-line px-5 py-4">
          <Building2 className="h-4 w-4 text-muted" /> By Org
        </div>
        {data && data.by_org.length > 0 ? (
          <ul className="divide-y divide-line">
            {data.by_org.map((r) => (
              <li key={r.org_id} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="font-mono text-xs text-muted">{r.org_id}</span>
                <span className="font-medium tabular-nums text-ink">{r.units}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={Gauge}
            title="No metered usage yet"
            hint="Launch a campaign to start metering."
          />
        )}
      </Card>

      {data && data.by_period && data.by_period.length > 0 && (
        <Card className="mt-4">
          <div className="eyebrow flex items-center gap-2 border-b border-line px-5 py-4">
            <Gauge className="h-4 w-4 text-muted" /> By Period
          </div>
          <ul className="divide-y divide-line">
            {data.by_period.map((r) => (
              <li key={r.period} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="font-mono text-xs text-muted">{r.period}</span>
                <span className="font-medium tabular-nums text-ink">{r.units}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
